/**
 * No modo "Próximos dias", a legenda do mapa (o lugar da legenda do satélite):
 * a variável na tela e a escala com números e unidade. A variável se troca nos
 * botões da previsão do trilho (`BotoesDaPrevisao`, 18/10/2026), como as
 * camadas; aqui fica só o nome dela.
 *
 * Celular: do trilho de camadas até a borda direita, logo acima da barra
 * (o play flutuante e o botão do rastreio somem no modo), em UMA linha: o nome
 * e a escala — ≤ 64 px de altura (julgamento de
 * `docs/proposta-previsao-entrada.md`). A altura vai para
 * `--legenda-previsao-altura`, que põe o crédito da foto de fundo logo acima dela
 * (index.css). Some com a folha da previsão aberta.
 * Desktop: canto inferior esquerdo, ao lado do painel lateral quando ele está aberto.
 */
import clsx from 'clsx';
import { ChevronDown } from 'lucide-react';
import { useLayoutEffect, useRef, type RefObject } from 'react';
import { useDeitado } from '../ui/useMediaQuery';
import { SIDEBAR_WIDTH } from '../map/viewControl';
import { useUiStore } from '../state/ui';
import { usePainelLateralAberto } from '../ui/hooks';
import { CURTOS, ICONES } from './BotoesDaPrevisao';
import { camadaDe, formatarValor, type Paleta } from './camadas';
import { tempoEmUso, usePrevisaoStore } from './store';

/** A altura da legenda (celular) em `--legenda-previsao-altura`, para o crédito da foto de fundo ficar acima dela. */
function useAlturaNoDocumento(ref: RefObject<HTMLElement | null>, ativo: boolean): void {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!ativo || !el) return;
    const raiz = document.documentElement;
    const medir = () => raiz.style.setProperty('--legenda-previsao-altura', `${Math.ceil(el.getBoundingClientRect().height)}px`);
    medir();
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(medir);
    ro?.observe(el);
    return () => {
      ro?.disconnect();
      raiz.style.removeProperty('--legenda-previsao-altura');
    };
  });
}

export function Escala(props: { paleta: Paleta; compacta?: boolean; poucas?: boolean }) {
  const { paleta } = props;
  const ps = paleta.paradas.filter((p) => p.v > -50);
  // A lista com os nomes das faixas (a fumaça) não cabe na legenda de uma linha: lá vão os números.
  if (paleta.tipo === 'classes' && ps.every((p) => p.nome) && !props.poucas) {
    return (
      <ul className="flex flex-wrap gap-x-2.5 gap-y-0.5 text-xs text-ink-200" aria-label="Escala">
        <li className="flex items-center gap-1">
          <span className="size-2.5 rounded-sm border border-ink-400" aria-hidden />
          Boa (sem cor)
        </li>
        {ps.map((p) => (
          <li key={p.v} className="flex items-center gap-1">
            <span className="size-2.5 rounded-sm" style={{ background: p.cor }} aria-hidden />
            {p.nome} ≥ {formatarValor(paleta, p.v)}
          </li>
        ))}
        <li className="text-ink-200">{paleta.unidade}</li>
      </ul>
    );
  }
  const gradiente =
    paleta.tipo === 'classes'
      ? ps.map((p, i) => `${p.cor} ${(i / ps.length) * 100}% ${((i + 1) / ps.length) * 100}%`).join(', ')
      : ps.map((p, i) => `${p.cor} ${(i / (ps.length - 1)) * 100}%`).join(', ');
  // Poucas faixas (a chance dos cenários): cada número embaixo da fronteira da sua cor, todos (nenhum some no celular).
  if (paleta.tipo === 'classes' && ps.length <= 6) {
    return (
      <div aria-label="Escala">
        <div className="h-2.5 rounded-sm" style={{ background: `linear-gradient(to right, ${gradiente})` }} />
        <div className="relative mt-0.5 h-4 font-mono text-xs text-ink-200 tabular-nums">
          {ps.map((p, i) => (
            <span key={p.v} className={clsx('absolute top-0', i > 0 && '-translate-x-1/2')} style={{ left: `${(i / ps.length) * 100}%` }}>
              {formatarValor(paleta, p.v)}
            </span>
          ))}
          {paleta.unidade && <span className="absolute top-0 right-0 text-ink-200">{paleta.unidade}</span>}
        </div>
      </div>
    );
  }
  // Compacta (celular): só uma marca sim, outra não, para os números não se tocarem.
  // Poucas (a legenda de uma linha do celular): a primeira, duas do meio e a última.
  const marcas = props.poucas && ps.length > 4
    ? [...new Set([0, Math.round((ps.length - 1) / 3), Math.round((2 * (ps.length - 1)) / 3), ps.length - 1])].map((i) => ps[i]!)
    : props.compacta && ps.length > 7
      ? ps.filter((_, i) => i % 2 === 0 || i === ps.length - 1)
      : ps;
  return (
    <div aria-label="Escala">
      <div className="h-2.5 rounded-sm" style={{ background: `linear-gradient(to right, ${gradiente})` }} />
      <div className={clsx('mt-0.5 flex justify-between font-mono text-xs text-ink-200 tabular-nums', props.poucas && 'gap-x-1.5')}>
        {marcas.map((p) => (
          // Poucas: sem o ",0" (1 em vez de 1,0), para caber em 360 px sem colar os números.
          <span key={p.v}>{props.poucas ? formatarValor(paleta, p.v).replace(/,0$/, '') : formatarValor(paleta, p.v)}</span>
        ))}
        {paleta.unidade && <span className="text-ink-200">{paleta.unidade}</span>}
      </div>
    </div>
  );
}

export function LegendaFlutuante(props: { mobile: boolean }) {
  const modo = usePrevisaoStore((s) => s.modo);
  const variavel = usePrevisaoStore((s) => s.variavel);
  const fumaca = usePrevisaoStore((s) => s.fumaca);
  const cenario = usePrevisaoStore((s) => s.cenario);
  const painel = usePainelLateralAberto() && !props.mobile ? SIDEBAR_WIDTH : 0;
  const folha = useUiStore((s) => s.sheetOpen);
  const painelDaPrevisao = usePrevisaoStore((s) => s.painel);
  const tempo = usePrevisaoStore(tempoEmUso);
  const escolha = usePrevisaoStore((s) => s.legendaRecolhida);
  const deitado = useDeitado() && props.mobile;
  const recolhida = escolha ?? deitado;
  const ref = useRef<HTMLElement>(null);
  const visivel = modo && !(props.mobile && (folha || painelDaPrevisao));
  useAlturaNoDocumento(ref, visivel && props.mobile);
  if (!visivel) return null;
  const camada = camadaDe(variavel, fumaca, cenario, tempo);
  const Icone = ICONES[variavel];
  const recolher = (v: boolean) => usePrevisaoStore.setState({ legendaRecolhida: v });
  // Celular: logo acima da linha do pé (Voltar · relógio · play, previsao/BarraDaPrevisao `PeDaPrevisao`: 8 + 44 + 8 px).
  const lugar = props.mobile
    ? deitado
      ? { left: 'var(--trilho-fim)', bottom: '60px', width: 'min(calc(100% - var(--trilho-fim) - 64px), 420px)' }
      : // Em pé, de borda a borda (8 px), alinhada com o "Voltar" e o ‹: o trilho termina acima dela.
        { left: 'calc(8px + env(safe-area-inset-left))', right: 'calc(8px + env(safe-area-inset-right))', bottom: '60px' }
    : { left: 12 + painel, bottom: 12, width: 320 };

  if (recolhida) {
    return (
      <section ref={ref} data-legenda-previsao="" aria-label="Legenda da previsão" className="absolute z-10" style={{ ...lugar, width: undefined, right: undefined }}>
        <button
          type="button"
          onClick={() => recolher(false)}
          aria-expanded={false}
          className={clsx(
            'glass inline-flex items-center gap-1.5 px-3 text-xs font-semibold text-ink-100 shadow-lg hover:bg-ink-800 focus-visible:outline-2 focus-visible:outline-violet-400',
            // Celular: 44 px e cantos de 12, como o "Voltar" logo abaixo dela; desktop, a pílula.
            props.mobile ? 'h-11 rounded-xl' : 'min-h-9 rounded-full pointer-coarse:min-h-11',
          )}
        >
          <Icone className="size-3.5 text-violet-300" aria-hidden />
          {camada.rotulo} · legenda
        </button>
      </section>
    );
  }

  if (props.mobile) {
    return (
      <section ref={ref} data-legenda-previsao="" aria-label="Legenda da previsão" className="glass absolute z-10 flex items-center gap-2 rounded-xl py-1 pr-1 pl-2 shadow-lg" style={lugar}>
        <span className="flex w-[3.75rem] shrink-0 flex-col items-center gap-0.5 text-[11px] leading-none font-semibold text-violet-100" data-variavel-da-legenda="">
          <Icone className="size-4 text-violet-300" aria-hidden />
          {/* Os cenários: o limiar no lugar do nome (o rótulo inteiro está na legenda larga e no `title`). */}
          {variavel === 'cenarios' ? (cenario === 'forte' ? '≥ 10 mm' : '≥ 1 mm') : CURTOS[variavel]}
        </span>
        <div className="min-w-0 flex-1">
          <Escala paleta={camada.paleta} compacta poucas />
        </div>
        <button
          type="button"
          onClick={() => recolher(true)}
          aria-expanded
          aria-label="Recolher a legenda"
          title="Recolher a legenda"
          className="inline-flex size-11 shrink-0 items-center justify-center rounded-lg text-ink-300 hover:bg-ink-700 hover:text-white focus-visible:outline-2 focus-visible:outline-violet-400"
        >
          <ChevronDown className="size-4" aria-hidden />
        </button>
      </section>
    );
  }

  return (
    <section
      data-legenda-previsao=""
      aria-label="Legenda da previsão"
      className={clsx('glass absolute z-10 flex flex-col rounded-xl p-2 shadow-lg', deitado ? 'gap-1' : 'gap-1.5')}
      style={lugar}
    >
      <div className="flex items-center gap-1.5">
        <Icone className="size-4 shrink-0 text-violet-300" aria-hidden />
        <span className="min-w-0 flex-1 truncate text-xs font-semibold text-ink-100" data-variavel-da-legenda="">
          {camada.rotulo}
          <span className="font-normal text-ink-300">
            {camada.id === 'chuva'
              ? ' · média de cada intervalo'
              : camada.id === 'fumaca' && fumaca === 'pm25'
                ? ' · valor horário'
                : camada.id === 'cenarios'
                  ? ' em 6 h · 64 cenários'
                  : ''}
          </span>
        </span>
        <button
          type="button"
          onClick={() => recolher(true)}
          aria-expanded
          aria-label="Recolher a legenda"
          title="Recolher a legenda"
          className="inline-flex size-7 shrink-0 items-center justify-center rounded-lg text-ink-300 hover:bg-ink-700 hover:text-white focus-visible:outline-2 focus-visible:outline-violet-400"
        >
          <ChevronDown className="size-4" aria-hidden />
        </button>
      </div>
      <Escala paleta={camada.paleta} compacta={props.mobile} />
    </section>
  );
}
